import { getToken } from "@/core/api/requests/getToken";
import { sanitizeParams } from "@/core/utils/sanitizeParams";
import { action, atom, batch, reatomAsync, withCache } from "@reatom/framework";
import { initDataRaw } from "@telegram-apps/sdk-react";
import { AxiosError } from "axios";
import { getSearch } from "../../../api/requests/search";
import { getCountryAction } from "../city";
import { ProfGroupDetailInterface, ProfGroupInterface } from "../profGroups";
import {
  currentSubCategories,
  profGroupDetailAtom,
} from "../profGroups/profGroups";
import { userAtom } from "../user/user";
import { SearchInterface, SearchParams } from "./types";

export const searchAtom = atom<SearchInterface | null>(null, "searchAtom");
export const isLoadingSearchAtom = atom(false, "isLoadingSearchAtom");
export const receivedAtom = atom(0, "receivedAtom");

export const searchListScrollPositionAtom = atom(0);

export const paramsAtom = atom<SearchParams>({}, "paramsAtom");
export const selectedProfessionsAtom = atom<ProfGroupDetailInterface[]>(
  [],
  "selectedProfessionsAtom"
);
export const selectedCategoriesAtom = atom<ProfGroupInterface[] | null>(
  null,
  "selectedCategoriesAtom"
);
export const searchInputAtom = atom<string>("", "searchInputAtom");
export const professionIdsAtom = atom<number[]>([], "professionIdsAtom");

export const searchAction = reatomAsync(
  async (ctx, { params, next }: { params: SearchParams; next?: boolean }) => {
    if (!next) searchAtom(ctx, null);
    isLoadingSearchAtom(ctx, true);

    const response = await ctx.schedule(() =>
      getSearch(sanitizeParams(params))
    );
    return { response, next };
  },
  {
    name: "searchAction",
    onFulfill: (ctx, { response, next }) => {
      batch(ctx, () => {
        const prev = ctx.get(searchAtom);

        searchAtom(
          ctx,
          next && prev
            ? { ...response, items: [...prev.items, ...response.items] }
            : response
        );

        receivedAtom(ctx, response.items.length);
        isLoadingSearchAtom(ctx, false);
      });
    },
    onReject: (ctx, error) => {
      isLoadingSearchAtom(ctx, false);
      if (error instanceof AxiosError && error.status === 401) {
        getToken(initDataRaw() || "").then((access) => {
          userAtom(ctx, access);
          getCountryAction(ctx);
          searchAction(ctx, { params: ctx.get(paramsAtom) });
        });
      }
      //
    },
  }
).pipe(withCache({ swr: false }));

export const updateParamsAction = action(
  (ctx, payload: Partial<SearchParams>) => {
    const current = ctx.get(paramsAtom);
    paramsAtom(ctx, {
      ...current,
      ...payload,
      profession_ids:
        payload.profession_ids !== undefined
          ? payload.profession_ids
          : current.profession_ids,
    });
  },
  "updateParamsAction"
);

// export const toggleProfessionsIdAction = action(
//   (ctx, professionId?: number) => {
//     if (professionId) {
//       const currentIds = ctx.get(professionIdsAtom) || [];

//       const updatedIds = currentIds.includes(professionId)
//         ? currentIds.filter((id) => id !== professionId)
//         : [...currentIds, professionId];
//       const details = ctx.get(profGroupDetailAtom) || [];
//       batch(ctx, () => {
//         updateParamsAction(ctx, {
//           profession_ids: updatedIds.length ? updatedIds : [],
//         });
//         professionIdsAtom(ctx, updatedIds);
//         selectedProfessionsAtom(
//           ctx,
//           details.filter(({ id }) => updatedIds.includes(id))
//         );
//       });
//     }

//     const currentIds = ctx.get(professionIdsAtom) || [];
//     if ((!professionId && !currentIds.length) || !currentIds.length) {
//       const currentNonSelectedCategories = ctx.get(currentSubCategories) || [];
//       const currentNonSelectedIds = currentNonSelectedCategories.map(
//         (item) => item.id
//       );
//       updateParamsAction(ctx, {
//         profession_ids: currentNonSelectedIds,
//       });
//     }
//   },
//   "toggleProfessionsIdAction"
// );
export const toggleProfessionsIdAction = action(
  (ctx, professionId?: number) => {
    if (professionId) {
      const currentIds = ctx.get(professionIdsAtom) || [];

      const updatedIds = currentIds.includes(professionId)
        ? currentIds.filter((id) => id !== professionId)
        : [...currentIds, professionId];
      const details = ctx.get(profGroupDetailAtom) || [];
      const nonSelectedCategories = new Set(
        details
          .filter(({ id }) => updatedIds.includes(id))
          .map(({ prof_group }) => prof_group.id)
      );
      const currentNonSelectedCategories = ctx.get(currentSubCategories) || [];
      const currentNonSelectedIds = currentNonSelectedCategories
        .filter(({ prof_group }) => !nonSelectedCategories.has(prof_group.id))
        .map((item) => item.id);
      batch(ctx, () => {
        updateParamsAction(ctx, {
          profession_ids: updatedIds.length
            ? [...updatedIds, ...currentNonSelectedIds]
            : [],
        });
        professionIdsAtom(ctx, updatedIds);
        selectedProfessionsAtom(
          ctx,
          details.filter(({ id }) => updatedIds.includes(id))
        );
      });
    }

    const currentIds = ctx.get(professionIdsAtom) || [];
    if ((!professionId && !currentIds.length) || !currentIds.length) {
      const currentNonSelectedCategories = ctx.get(currentSubCategories) || [];
      const currentNonSelectedIds = currentNonSelectedCategories.map(
        (item) => item.id
      );
      updateParamsAction(ctx, {
        profession_ids: currentNonSelectedIds,
      });
    }
  },
  "toggleProfessionsIdAction"
);
paramsAtom.onChange((ctx, params) => {
  searchAction(ctx, { params });
});

export const resetProfessionsDetailsAction = action((ctx, id) => {
  const selected = ctx.get(selectedCategoriesAtom);
  const selectedProfessions = ctx.get(selectedProfessionsAtom) || [];

  const current = selected?.filter((item) => item.id !== id) || [];
  const currentProf = selectedProfessions.filter(
    (item) => item.prof_group.id !== id
  );

  // const updatedIds = current.profession_ids?.filter((item) => item !== id);
  selectedCategoriesAtom(ctx, current);
  selectedProfessionsAtom(ctx, currentProf);

  batch(ctx, () => {
    // selectedCategoriesAtom(ctx, null);
    // selectedProfessionsAtom(ctx, []);
    professionIdsAtom(
      ctx,
      currentProf.map((item) => item.id)
    );
    selectedCategoriesAtom(ctx, current);
    selectedProfessionsAtom(ctx, currentProf);
    updateParamsAction(ctx, {
      profession_ids: undefined,
    });
    // ctx.schedule(() => searchAction(ctx, { params: {} }));
  });
});

export const toggleCategoryAction = action((ctx, item) => {
  const selectedCategories = ctx.get(selectedCategoriesAtom) ?? [];
  const selectedProfessions = ctx.get(selectedProfessionsAtom);

  if (selectedCategories.some((cat) => cat.id === item.id)) {
    selectedCategoriesAtom(
      ctx,
      selectedCategories.filter((cat) => cat.id !== item.id)
    );
  } else {
    selectedCategoriesAtom(ctx, [...selectedCategories, item]);
  }

  if (
    selectedProfessions.some(
      (prof) => !selectedCategories.some((cat) => cat.id === prof.prof_group.id)
    )
  ) {
    selectedProfessionsAtom(ctx, []);
  }
}, "toggleCategoryAction");
// export const toggleCategoryAction = action((ctx, item) => {
//   const selectedCategory = ctx.get(selectedCategoriesAtom);
//   const selectedProfessions = ctx.get(selectedProfessionsAtom);

//   if (selectedCategory?.id !== item.id) {
//     selectedCategoriesAtom(ctx, item);
//   } else if (selectedCategory !== null) {
//     if (selectedProfessions.length) {
//       resetProfessionsDetailsAction(ctx);
//     } else selectedCategoriesAtom(ctx, null);
//   }

//   if (selectedCategory?.id === selectedProfessions[0]?.prof_group.id) {
//     selectedProfessionsAtom(ctx, []);
//   }
// }, "toggleCategoryAction");

export const freelanceAtom = atom<SearchInterface | null>(
  null,
  "freelanceAtom"
);
export const isLoadingFreelanceAtom = atom(false, "isLoadingFreelanceAtom");

export const freelanceAction = reatomAsync(
  async (_ctx, { user_id }: { user_id: number }) => {
    const response = await getSearch({ user_id });
    // freelanceAtom(ctx, response);
    return response;
  },
  {
    name: "FreelanceAction",
    onFulfill: (ctx, response) => {
      batch(ctx, () => {
        freelanceAtom(ctx, response);
        isLoadingFreelanceAtom(ctx, false);
      });
    },
  }
);
